import { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { Outlet } from 'react-router-dom';

import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import { ChevronRight, Loader2 } from 'lucide-react';

import { useGetProjectById } from '../hooks';
import { useGetDatasetById } from '../hooks/tanstack/useDatasetsHook';
import { useGetDataTableByName } from '../hooks/tanstack/useDataTablesHook';
import {
  isOrgAdmin,
  useCustomNavigate,
  useGetDistinguishModel,
  useGetRouteParams,
} from '../hooks/tanstack/useUtilsHook';
import NotFound from '../icons/NotFound';
import routes from '../routes';
import cn from '../utils/cn';
import { useCurrentOrganization } from '../utils/helpers';

import CustomDrawer from './drawer/CustomDrawer';

const TriggerButton = ({
  open,
  handleDrawerClose,
  handleDrawerOpen,
}: {
  open: boolean;
  handleDrawerClose: () => void;
  handleDrawerOpen: () => void;
}) => (
  <button
    onClick={open ? handleDrawerClose : handleDrawerOpen}
    className={cn(
      'absolute shadow-md -left-2 z-50 bg-white w-5 h-5 flex items-center justify-center overflow-hidden rounded-full border border-[#E2E8F0]  p-1 transition'
    )}
  >
    {open ? (
      <ArrowBackIosNew
        style={{
          height: '16px',
          width: '16px',
        }}
      />
    ) : (
      <ArrowForwardIos
        style={{
          height: '16px',
          width: '16px',
        }}
      />
    )}
  </button>
);

const BreadCrumbs = ({
  open,
  handleOpenDrawer,
  handleCloseDrawer,
}: {
  open: boolean;
  handleOpenDrawer: () => void;
  handleCloseDrawer: () => void;
}) => {
  const navigate = useCustomNavigate();

  const [searchParams] = useSearchParams();

  const { projectId, orgId, modelId, datasetId, inputTableName, scenarioId } =
    useGetRouteParams();

  const { isRunningModelOutputServer } = useGetDistinguishModel();

  const { data: project } = useGetProjectById(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const isActual = searchParams.get('isActualData') === 'true';

  const { model } = useGetDistinguishModel({
    isActual,
  });

  const { data: dataset } = useGetDatasetById(
    {
      datasetId,
      orgId,
      projectId,
    },
    {
      enabled: !!datasetId && !!orgId && !!projectId,
    }
  );

  const { data: inputDataTable } = useGetDataTableByName(
    {
      orgId,
      projectId,
      dataTableName: inputTableName ?? '',
      modelId,
    },
    {
      enabled: !!inputTableName && !!orgId && !!projectId && !!modelId,
    }
  );

  return (
    <div className=" bg-white w-full sticky top-[60px] z-50 justify-between flex items-center">
      <div className="w-full grow flex items-center">
        <TriggerButton
          open={open}
          handleDrawerClose={handleCloseDrawer}
          handleDrawerOpen={handleOpenDrawer}
        />
        <div className="flex w-full px-12 py-4">
          {project && (
            <div className="flex items-center gap-2 text-[#666666]">
              <Link
                to={`/${orgId}/projects`}
                className="hover:text-black transition"
              >
                Projects
              </Link>
              <ChevronRight size={16} color="#666666" />
              <Link
                to={`/${orgId}/projects/${project.id}`}
                className="hover:text-black transition"
              >
                {project.name}
              </Link>
            </div>
          )}
          {model && (
            <>
              <div className="flex items-center gap-2 pl-2 text-[#666666]">
                <ChevronRight size={16} color="#666666" />
                <p
                  onClick={() => {
                    navigate(routes.projects.model.index, {
                      org_id: orgId.toString(),
                      project_id: projectId.toString(),
                    });
                  }}
                  className="hover:text-black cursor-pointer transition"
                >
                  Models
                </p>
              </div>
              <div className="flex items-center gap-2 pl-2 text-[#666666]">
                <ChevronRight size={16} color="#666666" />
                <p
                  onClick={() => {
                    navigate(routes.projects.model.detail, {
                      org_id: orgId.toString(),
                      project_id: projectId.toString(),
                      model_id: model.id.toString(),
                    });
                  }}
                  className="hover:text-black cursor-pointer transition"
                >
                  {model.name}
                </p>
              </div>
            </>
          )}
          {dataset && (
            <>
              <div className="flex items-center gap-2 pl-2 text-[#666666]">
                <ChevronRight size={16} color="#666666" />
                <p
                  onClick={() => {
                    navigate(routes.projects.dataset.index, {
                      org_id: orgId.toString(),
                      project_id: projectId.toString(),
                    });
                  }}
                  className="hover:text-black cursor-pointer transition"
                >
                  Datasets
                </p>
              </div>
              <div className="flex items-center gap-2 pl-2 text-[#666666]">
                <ChevronRight size={16} color="#666666" />
                <p
                  onClick={() => {
                    navigate(routes.projects.dataset.detail, {
                      org_id: orgId.toString(),
                      project_id: projectId.toString(),
                      dataset_id: dataset.id.toString(),
                    });
                  }}
                  className="hover:text-black cursor-pointer transition"
                >
                  {dataset.name}
                </p>
              </div>
            </>
          )}

          {inputDataTable && (
            <>
              <div className="flex items-center gap-2 pl-2 text-[#666666]">
                <ChevronRight size={16} color="#666666" />
                <p
                  onClick={() => {
                    navigate(`${routes.projects.model.detail}?tab=scenario`, {
                      org_id: orgId.toString(),
                      project_id: projectId.toString(),
                      model_id: modelId.toString(),
                    });
                  }}
                  className="hover:text-black cursor-pointer transition"
                >
                  Scenario
                </p>
              </div>
              <div className="flex items-center gap-2 pl-2 text-[#666666]">
                <ChevronRight size={16} color="#666666" />
                <p
                  onClick={() => {
                    navigate(
                      `${routes.projects.model.inputTable.index}?scenario_id=${scenarioId}`,
                      {
                        org_id: orgId.toString(),
                        project_id: projectId.toString(),
                        model_id: modelId.toString(),
                        input_table_name: inputDataTable.name,
                      }
                    );
                  }}
                  className="hover:text-black cursor-pointer transition"
                >
                  {inputDataTable.display_name}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
      {isRunningModelOutputServer && (
        <div className="flex right-0 w-[250px] px-4 z-50 items-center gap-2">
          <Loader2 className="text-primary-bright-blue h-5 w-5 animate-spin" />
          <p className="text-primary-bright-blue font-semibold">
            Model(s) is running
          </p>
        </div>
      )}
    </div>
  );
};

const UnAuthorizedView = () => (
  <div className="flex justify-center text-[#666666] flex-col gap-3 items-center h-[80vh] w-full">
    <NotFound size={100} />
    <p>
      You may not have access to this project or it no longer exist in this
      organization.
    </p>
    <p>
      Go back to your{' '}
      <Link className="underline text-primary-bright-blue" to={'/'}>
        Home page
      </Link>{' '}
    </p>
  </div>
);

const AuthorizedRoute = () => {
  const [open, setOpen] = useState(true);
  const location = useLocation();

  const { orgId, projectId } = useGetRouteParams();

  const { data: currentOrg } = useCurrentOrganization();

  const { data: project, isFetchedAfterMount: isFetchedProject } =
    useGetProjectById(
      {
        orgId,
        projectId,
      },
      {
        enabled: !!projectId && !!orgId,
      }
    );

  if (currentOrg && isOrgAdmin(currentOrg.role) && project && !project.role) {
    return <UnAuthorizedView />;
  }

  if (!project && isFetchedProject) {
    return <UnAuthorizedView />;
  }

  if (project && project.onboarding_status === 'configuring') {
    return (
      <Navigate
        to={`/${currentOrg?.id}/projects/create/?step=2&project_id=${project.id}`}
        state={{ from: location }}
        replace
      />
    );
  } else if (project && project.onboarding_status === 'uploading_data') {
    return (
      <Navigate
        to={`/${currentOrg?.id}/projects/create/?step=3&project_id=${project.id}`}
        state={{ from: location }}
        replace
      />
    );
  } else if (project && project.onboarding_status === 'reviewing') {
    {
      return (
        <Navigate
          to={`/${currentOrg?.id}/projects/create/?step=4&project_id=${project.id}`}
          state={{ from: location }}
          replace
        />
      );
    }
  }

  const handleOpenDrawer = () => {
    setOpen(true);
  };

  const handleCloseDrawer = () => {
    setOpen(false);
  };

  return (
    <div className="w-full flex justify-between">
      <div className="flex w-full">
        <div className="relative flex">
          {projectId && <CustomDrawer open={open} />}
        </div>
        <div className="w-full">
          <BreadCrumbs
            open={open}
            handleOpenDrawer={handleOpenDrawer}
            handleCloseDrawer={handleCloseDrawer}
          />
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AuthorizedRoute;
